// src/App.jsx
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "./components/context/ThemeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Layout Components
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import NotificationBar from "./components/NotificationBar";

// Pages
import HomePage from "./components/pages/Home/Home";
import AuthPage from "./components/pages/Login/AuthPage";
import OAuthCallback from "./components/Auth/OAuthCallback";
import TwoFactorLogin from "./components/Auth/TwoFactorLogin";
import OtpVerification from "./components/Auth/OtpVerification";
import GameRoutes from "./routes/GameRoutes";
import AdminPanel from "./components/pages/Admin/Admin";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import TailwindTest from "./components/pages/Admin/TailwindTest";
import Leaderboard from "./components/pages/Leaderboard/Leaderboard";
import {
  default as Dashboard,
  DashboardPage,
  PaymentsPage,
  OrdersPage,
  AccountDetailsPage,
} from "./components/pages/Dashboard/Dashboard";
import PaymentSuccess from "./components/pages/Games/PaymentSuccess";
import PaymentCallback from "./components/pages/Games/PaymentCallback";
import BlogPage from "./components/Blog/BlogPage";
import BlogsListPage from "./components/Blog/BlogListPage";
import Aboutus from "./components/pages/About/About";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import RefundPolicy from "./components/RefundPolicy";
import MobileHome from "./components/pages/Home/MobileHome";
import MobileAccount from "./components/pages/Account/MobileAccount";
import MobileReports from "./components/pages/Reports/MobileReports";
import AdminPayments from "./components/pages/Admin/AdminPayments";
import AccountsList from "./components/pages/Account/Accounts";
import AccountDetails from "./components/pages/Account/AccountDetails";
import AdminAccounts from "./components/pages/Admin/AdminAccounts";
import VipBenefits from "./components/pages/VIP/VipBenefits";
import MyVipStatus from "./components/pages/VIP/MyVipStatus";
import VipManagement from "./components/admin/VipManagement";
import UnauthorizedPage from "./components/pages/Error/Unauthorized";
import NotFoundPage from "./components/pages/Error/NotFound";
import VipPage from "./components/pages/VIP/VipPage";
import TournamentPage from "./components/pages/Tournament/TournamentPage";
import AboutPage from "./components/pages/Tournament/AboutPage";
import TournamentDashboard from "./components/pages/Tournament/TournamentDashboard";
import LegalNotice from "./components/LegalNotice";

import LiveTournamentPage from "./components/pages/Tournament/LiveTournamentPage";
import LiveStreamPage from "./components/pages/Tournament/LiveStreamPage";
import Reseller from "./components/pages/Retailer/Reseller";
import ScrollToTop from "./components/ScrollToTop";
import MysteryBox from "./components/pages/VIP/MysteryBox";
import VipTiers from "./components/pages/VIP/VipTiers";
import AdminMysteryBox from "./components/pages/Admin/AdminMysteryBox";

import Avatar from "./components/Avatar/Avatar";
import MoodSeasonSwitcher from "./components/MoodSeasonSwitcher";
import AdminGameAccounts from "./components/admin/AdminGameAccounts";
import CreateTournamentPage from "./components/pages/Tournament/CreateTournamentPage";
import TournamentDetail from "./components/pages/Tournament/TournamentDetail";
import AnalyticsHomePage from "./components/pages/Analytics/AnalyticsHomePage";
import AnalyticsDashboard from "./components/pages/Analytics/components/AnalyticsDashboard";
import ProductAnalytics from "./components/pages/Analytics/components/ProductAnalytics";
import UserAnalytics from "./components/pages/Analytics/components/UserAnalytics";
import SupplierAnalytics from "./components/pages/Analytics/components/SuppliersAnalytics";
import AlertsManagement from "./components/pages/Analytics/components/AlertsManagement";
import VoucherRedeemPage from "./components/pages/Voucher/VoucherRedeemPage";
import VoucherPage from "./components/pages/Voucher/VoucherPage";
import VoucherCheckout from "./components/pages/Voucher/VoucherCheckout";
import AdminVouchers from "./components/admin/AdminVouchers";
import ReauthModal from "./components/Security/ReauthModal";
import { api } from "./utils/apiService";
import MobileBottomNav from "./components/pages/Home/MobileBottomNav";

// Detect mobile device
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

// Main App Content
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Zero Trust: Sudo Mode Re-authentication state
  const [isReauthOpen, setIsReauthOpen] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  useEffect(() => {
    const handleReauthRequired = (e) => {
      setPendingRequest(e.detail.config);
      setIsReauthOpen(true);
    };

    window.addEventListener("sudo-reauth-required", handleReauthRequired);
    return () =>
      window.removeEventListener("sudo-reauth-required", handleReauthRequired);
  }, []);

  const handleReauthSuccess = async (sudoToken) => {
    if (pendingRequest) {
      try {
        // Retry the original request that failed with the new sudo token
        const retryConfig = {
          ...pendingRequest,
          headers: {
            ...pendingRequest.headers,
            "x-sudo-token": sudoToken,
          },
        };
        await api(retryConfig);
        // After successful retry of a sensitive action, refreshing the page
        // ensures consistent state across the UI.
        window.location.reload();
      } catch (error) {
        console.error("Sudo retry failed:", error);
      }
    }
    setPendingRequest(null);
  };

  return (
    <div className="app min-h-screen flex flex-col">
      <Navbar />
      <NotificationBar />
      <MoodSeasonSwitcher />

      <ReauthModal
        isOpen={isReauthOpen}
        onClose={() => setIsReauthOpen(false)}
        onSuccess={handleReauthSuccess}
      />

      <main className="flex-grow">
        <ScrollToTop />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={isMobile ? <MobileHome /> : <HomePage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/login/2fa" element={<TwoFactorLogin />} />
          <Route path="/verify-otp/:email" element={<OtpVerification />} />
          <Route path="/verify-otp" element={<OtpVerification />} />
          <Route path="/register" element={<AuthPage />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/forgot-password" element={<AuthPage />} />
          <Route path="/reset-password/:token" element={<AuthPage />} />

          {/* Mobile-specific routes */}
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* tournament and store path */}
          <Route path="/tournament" element={<TournamentPage />} />
          <Route path="/tournament/about" element={<AboutPage />} />
          <Route
            path="/tournament/dashboard"
            element={<TournamentDashboard />}
          />

          <Route
            path="/tournament/create"
            element={
              <ProtectedRoute requiredRole="admin">
                <CreateTournamentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tournament/detail/:tournamentId"
            element={<TournamentDetail />}
          />

          <Route path="/tournament/live" element={<LiveTournamentPage />} />
          <Route
            path="/tournament/live/:streamId"
            element={<LiveStreamPage />}
          />

          <Route path="/retail" element={<Reseller />} />

          <Route
            path="/account"
            element={
              <ProtectedRoute>
                {isMobile ? (
                  <MobileAccount />
                ) : (
                  <Navigate to="/user-dashboard/my-account" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                {isMobile ? (
                  <MobileReports />
                ) : (
                  <Navigate to="/user-dashboard/orders" replace />
                )}
              </ProtectedRoute>
            }
          />


          <Route
            path="/redeem"
            element={
              <ProtectedRoute>
                <VoucherRedeemPage />
              </ProtectedRoute>
            }
          />
          <Route path="/vouchers" element={<VoucherPage />} />
          <Route
            path="/checkout/voucher"
            element={
              <ProtectedRoute>
                <VoucherCheckout />
              </ProtectedRoute>
            }
          />

          {/* Dashboard Routes */}
          <Route
            path="/user-dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />

            <Route path="payments" element={<PaymentsPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="my-account" element={<AccountDetailsPage />} />
            <Route path="refer-earn" element={<div>Refer & Earn</div>} />
            <Route path="query" element={<div>Query</div>} />
          </Route>
          <Route path="/avatar" element={<Avatar />} />

          {/* Games */}
          <Route path="/games/*" element={<GameRoutes />} />

          {/* Blog */}
          <Route path="/blogs" element={<BlogsListPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />

          {/* Static pages */}
          <Route path="/about" element={<Aboutus />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/legal-notice" element={<LegalNotice />} />

          {/* Payments */}
          <Route path="/payment/callback" element={<PaymentCallback />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />

          {/* VIP */}
          <Route path="/vip-benefits" element={<VipBenefits />} />
          <Route path="/vip-tiers" element={<VipTiers />} />
          <Route
            path="/my-vip"
            element={
              <ProtectedRoute>
                <MyVipStatus />
              </ProtectedRoute>
            }
          />
          <Route path="/vip" element={<VipPage />} />
          <Route path="/mystery-box" element={<MysteryBox />} />
          <Route
            path="/admin/vip"
            element={
              <ProtectedRoute requiredRole="admin">
                <VipManagement />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mystery-box"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminMysteryBox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/game-accounts"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminGameAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vouchers"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminVouchers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute requiredRole="admin">
                <AnalyticsHomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/products"
            element={
              <ProtectedRoute requiredRole="admin">
                <ProductAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/suppliers"
            element={
              <ProtectedRoute requiredRole="admin">
                <SupplierAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics/alerts"
            element={
              <ProtectedRoute requiredRole="admin">
                <AlertsManagement />
              </ProtectedRoute>
            }
          />

          {/* Leaderboard */}
          <Route path="/leaderboard" element={<Leaderboard />} />

          {/* Accounts */}
          <Route path="/accounts" element={<AccountsList />} />
          <Route path="/accounts/:id" element={<AccountDetails />} />

          {/* Tailwind test */}
          <Route path="/tailwind-test" element={<TailwindTest />} />

          {/* Error Pages */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
      <Footer />
    </div>
  );
}
// Root App Component
function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <AppContent />
        {/* Toast notifications globally accessible */}
        <ToastContainer position="bottom-right" autoClose={5000} />
      </div>
    </ThemeProvider>
  );
}

export default App;
